/** @jsx jsx */
import React, { useState } from 'react'
import styled from '@emotion/styled'
import IconTextBlock from './footer/IconTextBlock'
import { Grid, Box, jsx, Text, Heading, Flex } from 'theme-ui'
import ColumnLink from './footer/ColumnLink'
import SocialLink from './footer/SocialLink'
import CompanyInfo from './footer/CompanyInfo'
import { graphql } from 'gatsby'
import Separator from './shared/Separator/Separator'
import { HideOnMobile, HideOnDestop, Space } from '../components/shared/index'
import FooterAccordion from './shared/Accordion/Accordion'
import BottomMiscFooter from './footer/BottomMiscFooter'
import Link from './shared/Typography/Link/Link'
import { useWindowSize } from '../hooks/useWindowSize'
import TrustPilot from './TrustPilot'
import Input from '../components/shared/Input/Input'
import Button from '../components/shared/Buttons/Button'
import usePhoneSubscription from '../hooks/usePhoneSubscription'

const List = styled.ul`
  list-style-type: none;
`

const Wrapper = styled.footer`
  flex-flow: row wrap;
  margin: 0 auto;
`

export const GET_NAV = graphql`
  fragment ContentfulFooterDetails on ContentfulFooter {
    companyValues {
      value
      description {
        raw
      }
      icon {
        file {
          url
        }
      }
    }
    linkColumns {
      header
      hideHeader
      links {
        ... on ContentfulNavigationLink {
          navLinkText
          navLinkSlug
          color
        }
      }
    }
    socialLinks {
      platformName
      icon {
        file {
          url
        }
      }
    }
  }
`

const Footer = props => {
  const { mobile } = useWindowSize()
  const [phone, setPhone] = useState('')
  const [subscriptionStatus, setSubscriptionStatus] = useState('')

  const { sendSubscription: subscribePhone } = usePhoneSubscription()

  const cValuesLength = props.companyValues.length
  return (
    <Wrapper {...props}>
      <Box
        sx={{
          color: 'text',
          paddingTop: [35, null, null, 105],
          paddingBottom: [65, null, null, 125],
          bg: 'white',
        }}
      >
        <Grid
          sx={{
            maxWidth: cValuesLength > 3 ? '1000px' : '1400px',
            margin: 'auto',
          }}
          gap={[65, null, null, 100]}
          columns={[1, 1, 1, 1, cValuesLength > 3 ? 2 : cValuesLength]}
        >
          {props.companyValues.map((item, index) => (
            <IconTextBlock
              key={index}
              text={item.value}
              icon={item.icon.file.url}
              description={item.description}
            />
          ))}
        </Grid>
      </Box>
      {!props.miscOnly && (
        <Box
          sx={{
            p: ['35px 0px', null, null, null, '50px 60px 40px 60px'],
            bg: 'lightGray',
          }}
        >
          <Flex
            sx={{
              flexDirection: mobile ? 'column' : 'row',
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                flex: 1.7,
                margin: mobile ? '0px 40px' : 0,
              }}
            >
              <Text variant="signUpText" sx={{ mb: '8px' }}>
                Sign Up For Priority Access, Deals, and 10% Off Your First Order
              </Text>

              <Flex
                sx={{
                  width: '100%',
                  mb: '50px',
                  flexDirection: 'column',
                }}
              >
                <Flex
                  sx={{
                    flexDirection: 'row',
                  }}
                >
                  <Input
                    value={phone}
                    containerProps={{
                      sx: { width: mobile ? '75%' : '90%' },
                    }}
                    sx={{
                      bg: 'white',
                    }}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="Mobile Phone Number"
                  />
                  <Button
                    variant="default"
                    size={mobile ? 'default' : 'medium'}
                    placeholder={'Email'}
                    onClick={() => {
                      subscribePhone(phone)
                        .then(data => {
                          setPhone('')

                          switch (data.status) {
                            case 400:
                              setSubscriptionStatus(
                                'Please enter a valid phone number!'
                              )
                              break
                            case 201:
                              setSubscriptionStatus(
                                'Thank you for subscribing!'
                              )
                              break
                            case 200:
                              setSubscriptionStatus(
                                'Phone is already subscribed.'
                              )
                              break
                            default:
                              setSubscriptionStatus(
                                'An error occured, please try again!'
                              )
                              break
                          }
                        })
                        .catch(err => {
                          console.log(err)
                          setSubscriptionStatus('Thank you for subscribing!')
                        })
                    }}
                    styles={{
                      width: '10em',
                    }}
                  >
                    SUBMIT
                  </Button>
                </Flex>
                <Text
                  variant="body3"
                  sx={{
                    mt: '5px',
                  }}
                >
                  {subscriptionStatus}
                </Text>
              </Flex>

              <HideOnMobile>
                <Text variant="body2">
                  At JAXXON we only work with the most enduring and premium
                  quality materials straight from Italy, from solid 14k Gold to
                  925 Sterling Silver. Three years ago we committed to bringing
                  you Italian luxury at a price point within reach. We've never
                  settled for less and we don't believe that you should either.
                </Text>
              </HideOnMobile>
            </Flex>

            <Flex
              sx={{
                mt: mobile ? '24px' : 0,
                flex: 2.7,
                justifyContent: ['space-around', null, 'space-evenly'],
              }}
            >
              {props.linkColumns.map((item, index) => (
                <List key={index}>
                  <ColumnLink title={item.header} links={item.links} />
                </List>
              ))}
            </Flex>

            {mobile && (
              <Flex
                sx={{
                  flexDirection: 'column',
                  mt: '40px',
                }}
              >
                <SocialLink
                  title={'Be the First To Know'}
                  links={props.socialLinks}
                />

                <CompanyInfo
                  {...props}
                  img={props.brandLogo.file.url}
                  links={props.links}
                />

                <TrustPilot
                  data-style-height="24px"
                  data-style-width="100%"
                  data-theme="light"
                  style={{ flex: 1, marginTop: 10 }}
                />
              </Flex>
            )}

            <Flex
              sx={{
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <HideOnMobile>
                <SocialLink
                  title={'Be the First To Know'}
                  links={props.socialLinks}
                />
                <CompanyInfo
                  {...props}
                  address={props.address}
                  supportHours={props.supportHours}
                  supportNumber={props.supportNumber}
                  links={props.links}
                  img={props.brandLogo.file.url}
                />
              </HideOnMobile>
            </Flex>
          </Flex>
        </Box>
      )}
      <Separator sx={{ my: '0px' }} />

      <BottomMiscFooter />
      {props.spaceOnMobile && mobile && <Space height={60} block={true} />}
    </Wrapper>
  )
}

export default Footer
